import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import SEOMeta from '../../components/SEOMeta';
import { useDarkMode } from '../../hooks/useDarkMode';
import PixTab from './PixTab';
import CartaoTab from './CartaoTab';
import BoletoTab from './BoletoTab';
import FAQ from './FAQ';
import ShareButton from './ShareButton';
import PreviewCard from './PreviewCard';
import CouponBox, { type CupomAplicado } from './CouponBox';
import PhotoUpload from './PhotoUpload';

const FONT = "'DM Sans', sans-serif";
const FONT_DISPLAY = "'Source Serif 4', 'DM Sans', serif";

// TEMP: slugs que escondem pagamento e FAQ (cobrança manual via WhatsApp).
// Usado quando o cliente já está em atendimento direto com a equipe.
// Remover entrada quando a cobrança for feita / cliente pagar.
const HIDE_PAYMENT_SLUGS = new Set<string>(['maria-tereza-l91w']);

type CheckoutInfo = {
  ref_id: string;
  status: string;
  nome_homenageado: string;
  relacao: string;
  ocasiao: string;
  estilo: string;
  voice_gender: string;
  cupom_codigo: string | null;
  valor_final: number | null;
  previas_selecionadas: string | null;
  asaas_payment_id: string | null;
  pix_qr_code_payload: string | null;
  pix_qr_code_image: string | null;
  boleto_url: string | null;
  boleto_linha_digitavel: string | null;
  foto_url: string | null;
  previas: {
    a: { variante: 'A'; audio_url: string; duracao: number } | null;
    b: { variante: 'B'; audio_url: string; duracao: number } | null;
  };
  valores: { uma: number; ambas: number };
};

type Aba = 'pix' | 'cartao' | 'boleto';
type Selecao = { a: boolean; b: boolean };

type CupomPriced = { valor_base: number; valor_final: number; desconto: number };
type CupomCache = { codigo: string; one: CupomPriced | null; ab: CupomPriced | null } | null;

const cupomStorageKey = (slug: string) => `inspira-cupom-${slug}`;

async function validateCupomFor(slug: string, code: string, sel: 'A' | 'AB'): Promise<CupomPriced | null> {
  try {
    const r = await fetch('/api/checkout?action=validar-cupom', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, cupom: code, previas_selecionadas: sel }),
    });
    const d = await r.json().catch(() => ({}));
    if (!d?.valid) return null;
    return {
      valor_base: Number(d.valor_base),
      valor_final: Number(d.valor_final),
      desconto: Number(d.desconto),
    };
  } catch {
    return null;
  }
}

export default function LegacyCheckoutPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const isDark = useDarkMode();
  const [info, setInfo] = useState<CheckoutInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selecao, setSelecao] = useState<Selecao>({ a: false, b: false });
  const [aba, setAba] = useState<Aba>('pix');
  const [cupomCache, setCupomCache] = useState<CupomCache>(null);
  const [paymentVisible, setPaymentVisible] = useState(false);
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [cupomInvalidCode, setCupomInvalidCode] = useState<string | null>(null);

  const paymentSectionRef = useRef<HTMLDivElement | null>(null);
  const cupomHydratedRef = useRef(false);
  const selecaoHydratedRef = useRef(false);

  const urlCupom = useMemo(() => {
    const raw = searchParams.get('cupom');
    if (!raw) return null;
    const sanitized = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 24);
    return sanitized || null;
  }, [searchParams]);

  // Aceita ?v=A, ?v=B, ?v=AB pra pré-selecionar a variante (vindo do link da Isa pós-ESCOLHA_PREVIA).
  // Sanitiza pra string contendo só A e/ou B.
  const urlVariante = useMemo(() => {
    const raw = searchParams.get('v');
    if (!raw) return null;
    const sanitized = String(raw).toUpperCase().replace(/[^AB]/g, '').split('').sort().join('').slice(0, 2);
    return sanitized || null;
  }, [searchParams]);

  // Carrega dados do pedido
  useEffect(() => {
    if (!slug) {
      setError('Link inválido. Fale com a Inspira no WhatsApp.');
      setLoading(false);
      return;
    }
    fetch(`/api/checkout?action=info&slug=${encodeURIComponent(slug)}`)
      .then(async (r) => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.error || 'Pedido não encontrado');
        }
        return r.json();
      })
      .then((data: CheckoutInfo) => {
        setInfo(data);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  const selectionString = useMemo(() => {
    const v: string[] = [];
    if (selecao.a) v.push('A');
    if (selecao.b) v.push('B');
    return v.join('');
  }, [selecao]);

  const count = (selecao.a ? 1 : 0) + (selecao.b ? 1 : 0);

  // Cupom ativo derivado do cache + seleção atual.
  const cupom: CupomAplicado | null = useMemo(() => {
    if (!cupomCache) return null;
    const target = selectionString === 'AB' ? cupomCache.ab : cupomCache.one;
    if (!target) return null;
    return { codigo: cupomCache.codigo, desconto: target.desconto, valor_final: target.valor_final };
  }, [cupomCache, selectionString]);

  const valorBase = count === 2 ? info?.valores.ambas ?? 59.9 : info?.valores.uma ?? 49.9;
  const valorAtual = cupom ? cupom.valor_final : valorBase;

  // Aplica cupom rodando duas validações (A e AB) em paralelo. Persiste em sessionStorage no sucesso.
  const applyCupomCode = useCallback(async (rawCode: string, opts?: { silent?: boolean }) => {
    if (!slug) return false;
    const code = rawCode.trim().toUpperCase().slice(0, 24);
    if (!code) return false;
    const [resA, resAB] = await Promise.all([
      validateCupomFor(slug, code, 'A'),
      validateCupomFor(slug, code, 'AB'),
    ]);
    if (!resA && !resAB) {
      try { sessionStorage.removeItem(cupomStorageKey(slug)); } catch { /* noop */ }
      if (!opts?.silent) setCupomInvalidCode(code);
      return false;
    }
    setCupomCache({ codigo: code, one: resA, ab: resAB });
    setCupomInvalidCode(null);
    try { sessionStorage.setItem(cupomStorageKey(slug), code); } catch { /* noop */ }
    try {
      (window as any).gtag?.('event', 'cupom_aplicado', {
        slug,
        cupom: code,
        valor_uma: resA?.valor_final ?? null,
        valor_ambas: resAB?.valor_final ?? null,
      });
    } catch { /* gtag opcional */ }
    return true;
  }, [slug]);

  const removeCupom = useCallback(() => {
    setCupomCache(null);
    setCupomInvalidCode(null);
    if (slug) {
      try { sessionStorage.removeItem(cupomStorageKey(slug)); } catch { /* noop */ }
    }
  }, [slug]);

  // Pré-seleciona variante: prioridade URL (?v=A|B|AB) → DB (info.previas_selecionadas).
  // Roda 1x quando o pedido + prévias estão prontos. Cliente vem do link da Isa
  // com a escolha do WhatsApp já marcada — pode mudar pra "ambas" ou pra outra variante.
  useEffect(() => {
    if (selecaoHydratedRef.current) return;
    if (!info || !slug) return;
    const previaPronta = !!info.previas.a?.audio_url && !!info.previas.b?.audio_url;
    if (!previaPronta) return;
    const source = urlVariante || info.previas_selecionadas;
    if (!source) return;
    selecaoHydratedRef.current = true;
    setSelecao({ a: source.includes('A'), b: source.includes('B') });
    setPaymentVisible(true);
    requestAnimationFrame(() => {
      paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [info, slug, urlVariante]);

  // Hidrata cupom: prioridade URL → sessionStorage. Roda 1x quando o pedido + prévias estão prontos.
  useEffect(() => {
    if (cupomHydratedRef.current) return;
    if (!info || !slug) return;
    const previaPronta = !!info.previas.a?.audio_url && !!info.previas.b?.audio_url;
    if (!previaPronta) return;

    cupomHydratedRef.current = true;

    const fromStorage = (() => {
      try { return sessionStorage.getItem(cupomStorageKey(slug)); }
      catch { return null; }
    })();

    const codeToTry = urlCupom || fromStorage;
    if (!codeToTry) return;

    applyCupomCode(codeToTry, { silent: !urlCupom }).then((ok) => {
      if (!ok && urlCupom) {
        try {
          (window as any).gtag?.('event', 'cupom_url_invalido', { slug, cupom_tentado: urlCupom });
        } catch { /* gtag opcional */ }
      }
    });
  }, [info, slug, urlCupom, applyCupomCode]);

  const handleEscolher = useCallback((sel: 'A' | 'B' | 'AB') => {
    setSelecao({ a: sel.includes('A'), b: sel.includes('B') });
    setPaymentVisible(true);
    // Scroll suave pra área de pagamento depois do reveal.
    requestAnimationFrame(() => {
      paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const handleCouponBoxApply = useCallback((cup: CupomAplicado | null) => {
    if (!cup) {
      removeCupom();
      setShowCouponInput(false);
      return;
    }
    applyCupomCode(cup.codigo);
  }, [applyCupomCode, removeCupom]);

  const handlePagamentoConfirmado = useCallback(() => {
    if (slug) window.location.href = `/ouvir-musica/${slug}/sucesso`;
  }, [slug]);

  // Loading
  if (loading) {
    return <Shell><CenteredMessage>Abrindo o pedido…</CenteredMessage></Shell>;
  }

  // Error
  if (error || !info) {
    return (
      <Shell>
        <CenteredMessage>
          <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 12, color: 'var(--heading-ink)', letterSpacing: '-0.03em' }}>
            Não conseguimos abrir esse link
          </h1>
          <p style={{ fontSize: 15, color: 'var(--text-muted)', marginBottom: 22, lineHeight: 1.5, maxWidth: 360 }}>
            {error || 'Pedido não encontrado.'}
          </p>
          <a href="https://wa.me/5511926797492" style={ctaButton}>Falar com a Inspira</a>
        </CenteredMessage>
      </Shell>
    );
  }

  // Pago
  if (info.status === 'pago' || info.status === 'gerando_musica' || info.status === 'entregue') {
    return (
      <Shell>
        <CenteredMessage>
          <div aria-hidden style={{ marginBottom: 20 }}>
            <CheckCircle />
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 14, color: 'var(--heading-ink)', letterSpacing: '-0.03em' }}>
            Pagamento confirmado
          </h1>
          <p style={{ fontSize: 16, color: 'var(--text-muted)', marginBottom: 22, lineHeight: 1.5, maxWidth: 420 }}>
            A música pra <NameInline>{info.nome_homenageado}</NameInline> já entrou em produção. Em até 30 minutos a gente envia no WhatsApp. 💛
          </p>
          <a href="https://wa.me/5511926797492" style={ctaButton}>Voltar pro WhatsApp</a>
        </CenteredMessage>
      </Shell>
    );
  }

  const previaA = info.previas.a;
  const previaB = info.previas.b;
  const previaPronta = !!previaA?.audio_url && !!previaB?.audio_url;
  const ocultarPagamento = HIDE_PAYMENT_SLUGS.has(slug || '');

  return (
    <Shell>
      <SEOMeta
        title="Sua música — Inspira Músicas"
        description="Ouça as duas versões e escolha qual mais te emocionou"
        path={`/ouvir-musica/${slug}`}
        noIndex
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Hero */}
        <header style={{ padding: '40px 20px 14px', textAlign: 'center', maxWidth: 720, margin: '0 auto' }}>
          <img
            src={isDark ? '/logo-dark.png' : '/logo-light.png'}
            alt="Inspira Músicas"
            style={{ height: 36, objectFit: 'contain', marginBottom: 22 }}
          />

          <p style={{
            fontSize: 14,
            color: 'var(--text-muted)',
            margin: '0 0 4px',
            fontFamily: FONT,
            fontWeight: 500,
          }}>
            A música feita pra
          </p>

          <h1
            style={{
              fontFamily: FONT_DISPLAY,
              fontStyle: 'italic',
              fontWeight: 700,
              fontSize: 'clamp(46px, 11vw, 84px)',
              lineHeight: 0.95,
              letterSpacing: '-0.04em',
              color: 'var(--heading-ink)',
              margin: '0 0 14px',
              wordBreak: 'break-word',
            }}
          >
            {info.nome_homenageado || 'sua pessoa'}
          </h1>

          <p
            style={{
              fontSize: 16,
              color: 'var(--text-body)',
              maxWidth: 480,
              margin: '0 auto',
              lineHeight: 1.5,
              fontFamily: FONT,
              fontWeight: 400,
            }}
          >
            Compusemos duas versões. Ouça com calma e marque a que mais te emocionou.
          </p>

          {/* Meta sutil */}
          {(info.relacao || info.estilo || info.ocasiao) && (
            <div
              style={{
                marginTop: 16,
                fontSize: 12,
                color: 'var(--text-soft)',
                fontFamily: FONT,
                letterSpacing: '0.04em',
              }}
            >
              {[info.relacao && `pra sua ${info.relacao}`, info.estilo && info.estilo.toLowerCase(), info.ocasiao && info.ocasiao.toLowerCase()]
                .filter(Boolean)
                .join('  ·  ')}
            </div>
          )}
        </header>

        {/* Container principal */}
        <main
          style={{
            maxWidth: 720,
            margin: '0 auto',
            padding: '14px 18px 80px',
            fontFamily: FONT,
          }}
        >
          {/* Prévias */}
          <section style={{ marginTop: 22 }}>
            {!previaPronta ? (
              <PreviewSkeleton />
            ) : (
              <div className="preview-grid">
                <PreviewCard
                  variante="A"
                  audioUrl={previaA?.audio_url || null}
                  nomeHomenageada={info.nome_homenageado}
                  selected={selecao.a}
                />
                <PreviewCard
                  variante="B"
                  audioUrl={previaB?.audio_url || null}
                  nomeHomenageada={info.nome_homenageado}
                  selected={selecao.b}
                />
              </div>
            )}
          </section>

          {/* Banner de cupom — sutil, acima dos botões */}
          {previaPronta && !ocultarPagamento && cupom && (
            <div
              role="status"
              style={{
                marginTop: 18,
                marginBottom: 10,
                padding: '10px 14px',
                borderRadius: 12,
                background: 'rgba(31,138,91,0.08)',
                border: '1px solid rgba(31,138,91,0.28)',
                color: '#1f8a5b',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: FONT,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span aria-hidden>✓</span>
              <span>
                Cupom <strong style={{ letterSpacing: '0.06em' }}>{cupom.codigo}</strong> aplicado
                {(() => {
                  const base = cupom.valor_final + cupom.desconto;
                  if (base <= 0) return null;
                  const pct = Math.round((cupom.desconto / base) * 100);
                  if (pct < 1) return null;
                  return <span style={{ opacity: 0.85, fontWeight: 500 }}> · {pct}% off</span>;
                })()}
              </span>
            </div>
          )}
          {previaPronta && !ocultarPagamento && cupomInvalidCode && !cupom && (
            <div
              role="status"
              style={{
                marginTop: 18,
                marginBottom: 10,
                padding: '10px 14px',
                borderRadius: 12,
                background: 'var(--gold-pale)',
                border: '1px solid var(--gold-border)',
                color: 'var(--amber-deep)',
                fontSize: 12.5,
                fontFamily: FONT,
                lineHeight: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <span aria-hidden style={{ flexShrink: 0, marginTop: 1 }}>🎟️</span>
              <span style={{ flex: 1 }}>
                Não consegui aplicar o cupom <strong>{cupomInvalidCode}</strong>, mas você pode continuar com o preço normal.
              </span>
              <button
                type="button"
                onClick={() => setCupomInvalidCode(null)}
                aria-label="Fechar aviso"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--amber-deep)',
                  fontSize: 16,
                  lineHeight: 1,
                  cursor: 'pointer',
                  padding: 0,
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
          )}

          {/* Três botões de escolha */}
          {previaPronta && !ocultarPagamento && (
            <ChoiceButtons
              valorUma={info.valores.uma}
              valorAmbas={info.valores.ambas}
              cupomCache={cupomCache}
              selectionString={selectionString}
              onChoose={handleEscolher}
            />
          )}

          {/* Capa personalizada (opcional) — só após escolher */}
          {!ocultarPagamento && paymentVisible && count > 0 && (
            <PhotoUpload
              slug={slug || ''}
              initialFotoUrl={info.foto_url}
              nomeHomenageada={info.nome_homenageado}
            />
          )}

          {/* Pagamento — só após clicar num dos 3 botões */}
          {!ocultarPagamento && paymentVisible && count > 0 && (
            <section
              ref={paymentSectionRef}
              style={{
                position: 'relative',
                padding: '24px 22px 26px',
                borderRadius: 24,
                background: 'var(--surface-white)',
                border: '1.5px solid var(--border-soft)',
                boxShadow: '0 6px 28px rgba(0,0,0,0.04)',
                marginTop: 4,
                animation: 'inspiraFadeIn 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              <h2
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: 'var(--text-soft)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.16em',
                  margin: '0 0 14px',
                }}
              >
                Como você quer pagar
              </h2>

              {/* Tabs com PIX em destaque */}
              <div
                role="tablist"
                aria-label="Forma de pagamento"
                style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 8, marginBottom: 22 }}
              >
                <PaymentTab
                  active={aba === 'pix'}
                  onClick={() => setAba('pix')}
                  title="PIX"
                  hint="Mais rápido"
                  highlight
                />
                <PaymentTab active={aba === 'cartao'} onClick={() => setAba('cartao')} title="Cartão" hint="Crédito ou débito" />
                <PaymentTab active={aba === 'boleto'} onClick={() => setAba('boleto')} title="Boleto" hint="Compensa em 1-2 dias" />
              </div>

              <div>
                {aba === 'pix' && (
                  <PixTab
                    slug={slug || ''}
                    selectionString={selectionString}
                    valor={valorAtual}
                    cupomCodigo={cupom?.codigo || null}
                  />
                )}
                {aba === 'cartao' && (
                  <CartaoTab
                    slug={slug || ''}
                    selectionString={selectionString}
                    valor={valorAtual}
                    nomeSugerido={info.nome_homenageado}
                    cupomCodigo={cupom?.codigo || null}
                    onApproved={handlePagamentoConfirmado}
                  />
                )}
                {aba === 'boleto' && (
                  <BoletoTab
                    slug={slug || ''}
                    selectionString={selectionString}
                    nomeSugerido={info.nome_homenageado}
                    cupomCodigo={cupom?.codigo || null}
                  />
                )}
              </div>
            </section>
          )}

          {/* Selo Asaas (movido pra cima da Garantia/FAQ) */}
          {!ocultarPagamento && (
            <div
              style={{
                marginTop: 18,
                fontSize: 11.5,
                color: 'var(--text-soft)',
                textAlign: 'center',
                lineHeight: 1.6,
                fontFamily: FONT,
              }}
            >
              Pagamento processado pela Asaas, autorizada pelo Banco Central (cód. 461). Não armazenamos dados de cartão.
            </div>
          )}

          {/* Garantia + reassurance */}
          <div
            style={{
              marginTop: 18,
              padding: '16px 18px',
              borderRadius: 16,
              background: 'var(--gold-pale)',
              color: 'var(--amber-deep)',
              fontSize: 13.5,
              lineHeight: 1.55,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
            }}
          >
            <span aria-hidden style={{ flexShrink: 0, marginTop: 1 }}>💛</span>
            <span>
              <strong>Garantia total.</strong> Se a versão completa da música não ficar do jeito que você imaginou, a gente refaz sem custo.
            </span>
          </div>

          {/* FAQ */}
          {!ocultarPagamento && <FAQ />}

          {/* Saída de emergência: "Tenho um código de desconto" — discreto, abre input só quando clicado */}
          {!ocultarPagamento && previaPronta && !cupom && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              {!showCouponInput ? (
                <button
                  type="button"
                  onClick={() => setShowCouponInput(true)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-soft)',
                    fontSize: 12,
                    fontFamily: FONT,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: 4,
                  }}
                >
                  Tenho um código de desconto
                </button>
              ) : (
                <div style={{ marginTop: 6, textAlign: 'left' }}>
                  <CouponBox
                    slug={slug || ''}
                    selectionString={selectionString || 'A'}
                    applied={cupom}
                    onApply={handleCouponBoxApply}
                    initialCode={cupomInvalidCode || undefined}
                  />
                </div>
              )}
            </div>
          )}
          {!ocultarPagamento && previaPronta && cupom && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => { handleCouponBoxApply(null); }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-soft)',
                  fontSize: 12,
                  fontFamily: FONT,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 4,
                }}
              >
                Remover cupom {cupom.codigo}
              </button>
            </div>
          )}

          {/* Ações secundárias */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 24 }}>
            <ShareButton slug={slug || ''} cupomCodigo={cupom?.codigo || null} />
            <a
              href="https://wa.me/5511926797492"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '14px 12px',
                borderRadius: 999,
                background: 'transparent',
                color: 'var(--text-strong)',
                border: '1.5px solid var(--border-soft)',
                fontWeight: 700,
                fontSize: 13.5,
                fontFamily: FONT,
                textDecoration: 'none',
                transition: 'all 0.18s',
              }}
            >
              💬 Falar com a Inspira
            </a>
          </div>

          {/* Footer */}
          <footer
            style={{
              marginTop: 32,
              paddingTop: 20,
              borderTop: '1px solid var(--border-fade)',
              fontSize: 11.5,
              color: 'var(--text-soft)',
              textAlign: 'center',
              lineHeight: 1.6,
              fontFamily: FONT,
            }}
          >
            <div style={{ opacity: 0.85, marginBottom: 4 }}>
              AUTOMAT TECNOLOGIA DA INFORMAÇÃO LTDA · CNPJ 59.640.197/0001-76
            </div>
            <div style={{ opacity: 0.7 }}>
              Rua Pais Leme, 215, Conj. 1713, Sala 01, Andar 16 — Pinheiros, São Paulo/SP — CEP 05424-150
            </div>
          </footer>
        </main>
      </div>

      {/* Style global para a página de checkout */}
      <style>{`
        @keyframes inspiraFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .preview-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 14px;
          margin-bottom: 8px;
        }
        @media (min-width: 720px) {
          .preview-grid {
            grid-template-columns: 1fr 1fr;
            gap: 18px;
          }
        }
      `}</style>
    </Shell>
  );
}

// ─────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        position: 'relative',
        background: 'var(--cream)',
        overflow: 'hidden',
      }}
    >
      {/* Decorações ambientais (light/dark) — sutis */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse 800px 600px at 80% -10%, rgba(255,145,77,0.10), transparent 60%), radial-gradient(ellipse 700px 500px at -10% 80%, rgba(255,176,122,0.08), transparent 60%)',
        }}
      />
      {children}
    </div>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center',
        fontFamily: FONT,
        position: 'relative',
        zIndex: 1,
      }}
    >
      {children}
    </div>
  );
}

function NameInline({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontFamily: FONT_DISPLAY,
        fontStyle: 'italic',
        fontWeight: 700,
        color: 'var(--gold)',
      }}
    >
      {children}
    </span>
  );
}

function PaymentTab({
  active,
  onClick,
  title,
  hint,
  highlight,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  hint: string;
  highlight?: boolean;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        position: 'relative',
        padding: '12px 10px 10px',
        borderRadius: 14,
        border: active
          ? '1.5px solid var(--gold)'
          : highlight && !active
            ? '1.5px solid var(--gold-border)'
            : '1.5px solid var(--border-soft)',
        background: active
          ? 'linear-gradient(180deg, var(--gold) 0%, var(--gold-hover) 100%)'
          : highlight
            ? 'var(--gold-pale)'
            : 'transparent',
        color: active ? '#fff' : highlight ? 'var(--amber-deep)' : 'var(--text-strong)',
        fontFamily: FONT,
        cursor: 'pointer',
        transition: 'all 0.2s',
        textAlign: 'center',
        boxShadow: active ? '0 6px 18px rgba(255,145,77,0.32)' : 'none',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em' }}>{title}</div>
      <div style={{ fontSize: 10, fontWeight: 600, marginTop: 2, opacity: active ? 0.85 : 0.7, letterSpacing: '0.04em' }}>{hint}</div>
    </button>
  );
}

type ChoiceButtonsProps = {
  valorUma: number;
  valorAmbas: number;
  cupomCache: { codigo: string; one: { valor_final: number } | null; ab: { valor_final: number } | null } | null;
  selectionString: string;
  onChoose: (sel: 'A' | 'B' | 'AB') => void;
};

function ChoiceButtons({ valorUma, valorAmbas, cupomCache, selectionString, onChoose }: ChoiceButtonsProps) {
  const fmt = (v: number) => v.toFixed(2).replace('.', ',');
  const oneFinal = cupomCache?.one?.valor_final;
  const abFinal = cupomCache?.ab?.valor_final;
  const oneHasDiscount = oneFinal != null && oneFinal < valorUma - 0.01;
  const abHasDiscount = abFinal != null && abFinal < valorAmbas - 0.01;
  const onePrice = oneHasDiscount ? (oneFinal as number) : valorUma;
  const abPrice = abHasDiscount ? (abFinal as number) : valorAmbas;

  return (
    <div style={{ marginTop: 18, marginBottom: 22, display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
      <ChoiceBtn
        title="Gostei da Versão I"
        price={onePrice}
        baseStrike={oneHasDiscount ? valorUma : null}
        active={selectionString === 'A'}
        variant="primary"
        onClick={() => onChoose('A')}
        formatter={fmt}
      />
      <ChoiceBtn
        title="Gostei da Versão II"
        price={onePrice}
        baseStrike={oneHasDiscount ? valorUma : null}
        active={selectionString === 'B'}
        variant="primary"
        onClick={() => onChoose('B')}
        formatter={fmt}
      />
      <ChoiceBtn
        title="Quero levar as duas"
        price={abPrice}
        baseStrike={abHasDiscount ? valorAmbas : null}
        active={selectionString === 'AB'}
        variant="combo"
        onClick={() => onChoose('AB')}
        formatter={fmt}
      />
    </div>
  );
}

function ChoiceBtn({
  title,
  price,
  baseStrike,
  active,
  variant,
  onClick,
  formatter,
}: {
  title: string;
  price: number;
  baseStrike: number | null;
  active: boolean;
  variant: 'primary' | 'combo';
  onClick: () => void;
  formatter: (v: number) => string;
}) {
  const isCombo = variant === 'combo';
  const bg = isCombo
    ? 'linear-gradient(135deg, #cc6620 0%, #b35418 100%)'
    : 'linear-gradient(135deg, var(--gold) 0%, var(--gold-hover) 100%)';
  const shadow = active
    ? isCombo
      ? '0 14px 32px rgba(204,102,32,0.42)'
      : '0 14px 32px rgba(255,145,77,0.42)'
    : isCombo
      ? '0 6px 18px rgba(204,102,32,0.28)'
      : '0 6px 18px rgba(255,145,77,0.28)';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        width: '100%',
        minHeight: 64,
        padding: '14px 20px',
        borderRadius: 16,
        border: active ? '2px solid #fff' : 'none',
        outline: active ? '2px solid var(--gold)' : 'none',
        outlineOffset: active ? 1 : 0,
        background: bg,
        color: '#fff',
        fontFamily: FONT,
        fontWeight: 800,
        fontSize: 16,
        letterSpacing: '-0.01em',
        cursor: 'pointer',
        boxShadow: shadow,
        transform: active ? 'translateY(-1px)' : 'none',
        transition: 'transform 0.18s, box-shadow 0.18s',
        textAlign: 'left',
      }}
      onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(0) scale(0.985)')}
      onMouseUp={(e) => (e.currentTarget.style.transform = active ? 'translateY(-1px)' : 'none')}
      onMouseLeave={(e) => (e.currentTarget.style.transform = active ? 'translateY(-1px)' : 'none')}
    >
      <span style={{ flex: 1, lineHeight: 1.2 }}>{title}</span>
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
        {baseStrike != null && (
          <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.7, textDecoration: 'line-through', fontVariantNumeric: 'tabular-nums' }}>
            R$ {formatter(baseStrike)}
          </span>
        )}
        <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
          R$ {formatter(price)}
        </span>
      </span>
    </button>
  );
}

function PreviewSkeleton() {
  return (
    <div className="preview-grid">
      {[1, 2].map((i) => (
        <div
          key={i}
          style={{
            padding: 22,
            borderRadius: 24,
            background: 'var(--surface-white)',
            border: '1.5px solid var(--border-soft)',
            opacity: 0.6,
            minHeight: 170,
          }}
        >
          <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '40px 0' }}>
            Carregando prévia…
          </div>
        </div>
      ))}
    </div>
  );
}

function CheckCircle() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
      <circle cx="40" cy="40" r="38" stroke="var(--gold)" strokeWidth="2" fill="var(--gold-pale)" />
      <path
        d="M24 41.5L34 52L56 28"
        stroke="var(--gold)"
        strokeWidth="3.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ctaButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '15px 26px',
  borderRadius: 999,
  background: 'var(--gold)',
  color: '#fff',
  fontWeight: 700,
  fontSize: 15,
  fontFamily: FONT,
  textDecoration: 'none',
  letterSpacing: '-0.005em',
  boxShadow: '0 8px 28px rgba(255,145,77,0.38)',
};
